import { useEffect, useState, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Sparkles } from "lucide-react";

const AuthModal = lazy(() => import("@/components/AuthModal").then(m => ({ default: m.AuthModal })));
import { supabase } from "@/integrations/supabase/client";
import LandingNav from "@/components/landing/LandingNav";
import HeroSection from "@/components/landing/HeroSection";
import EbooksSection from "@/components/landing/EbooksSection";
import TestesSection from "@/components/landing/TestesSection";
import CursosSection from "@/components/landing/CursosSection";
import AppSection from "@/components/landing/AppSection";
import AboutSection from "@/components/landing/AboutSection";
import PersonasSection from "@/components/landing/PersonasSection";
import FaqSection from "@/components/landing/FaqSection";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import CtaSection from "@/components/landing/CtaSection";
import LandingFooter from "@/components/landing/LandingFooter";

const Index = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleLoginSuccess = async (user: any) => {
    setShowAuthModal(false);
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);
    const isConsultant = roles?.some((r) => r.role === "consultant");
    navigate(isConsultant ? "/consultor" : "/app");
  };

  // Detect recovery errors in hash and redirect to /reset-password
  useEffect(() => {
    const hash = location.hash;
    if (hash && (hash.includes("error_code=otp_expired") || hash.includes("error=access_denied"))) {
      navigate(`/reset-password${hash}`, { replace: true });
      return;
    }
    // Auto-scroll to anchor when coming from external link
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {!user && <LandingNav onOpenAuth={() => setShowAuthModal(true)} />}

      {user && (
        <Link
          to="/app"
          className="md:hidden fixed right-4 top-[calc(0.75rem+env(safe-area-inset-top,0px))] z-[70] flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-colors hover:bg-primary/90"
        >
          <Sparkles className="h-4 w-4" />
          App
        </Link>
      )}

      <HeroSection />
      <EbooksSection />
      <TestesSection />
      <CursosSection />
      <AppSection />
      <AboutSection />
      <PersonasSection />
      <FaqSection />
      <TestimonialsSection />
      <CtaSection />
      <LandingFooter />

      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onSuccess={() => setShowAuthModal(false)}
            defaultMode="login"
            onLoginSuccess={handleLoginSuccess}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
